import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

export interface ToastOptions {
  tone?: 'default' | 'call';
  ms?: number;
  undo?: () => void;
  /** 先頭に出すアイコン（Material Symbols の名前）。call のときは電話アイコン固定。 */
  icon?: string;
}

interface ToastItem extends ToastOptions {
  id: number;
  content: ReactNode;
  leaving?: boolean;
}

export interface AskOptions {
  title: string;
  text?: string;
  ok?: string;
  cancel?: string;
  tone?: 'primary' | 'danger';
  body?: ReactNode;
}

/** スマホのワンタップ操作にはさむ確認（押し間違い防止）。 */
export interface SheetOptions {
  action: string;
  name: string;
  patientId: string;
  dept: string;
  color: string;
}

interface UiValue {
  toast(content: ReactNode, options?: ToastOptions): void;
  ask(options: AskOptions): Promise<boolean>;
  confirmTap(options: SheetOptions): Promise<boolean>;
}

const UiContext = createContext<UiValue | null>(null);

const isPhone = () => window.matchMedia('(max-width: 760px)').matches;

export function UiProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const [dialog, setDialog] = useState<(AskOptions & { resolve: (v: boolean) => void }) | null>(
    null,
  );
  const [sheet, setSheet] = useState<(SheetOptions & { resolve: (v: boolean) => void }) | null>(
    null,
  );
  const nextId = useRef(1);

  const remove = useCallback((id: number) => {
    setToasts((list) => list.map((t) => (t.id === id ? { ...t, leaving: true } : t)));
    setTimeout(() => setToasts((list) => list.filter((t) => t.id !== id)), 260);
  }, []);

  const toast = useCallback(
    (content: ReactNode, options: ToastOptions = {}) => {
      const id = nextId.current++;
      setToasts((list) => [...list, { id, content, ...options }].slice(-3));
      setTimeout(() => remove(id), options.ms ?? 5000);
    },
    [remove],
  );

  const ask = useCallback(
    (options: AskOptions) => new Promise<boolean>((resolve) => setDialog({ ...options, resolve })),
    [],
  );

  const confirmTap = useCallback(
    (options: SheetOptions) =>
      isPhone()
        ? new Promise<boolean>((resolve) => setSheet({ ...options, resolve }))
        : Promise.resolve(true),
    [],
  );

  const value = useMemo<UiValue>(() => ({ toast, ask, confirmTap }), [toast, ask, confirmTap]);

  const closeDialog = (result: boolean) => {
    dialog?.resolve(result);
    setDialog(null);
  };
  const closeSheet = (result: boolean) => {
    sheet?.resolve(result);
    setSheet(null);
  };

  return (
    <UiContext.Provider value={value}>
      {children}

      <div className="toasts" id="toasts" aria-live="polite">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={`toast ${t.tone === 'call' ? 'is-call' : ''} ${t.leaving ? 'is-out' : ''}`}
          >
            {t.tone === 'call' || t.icon ? (
              <span className="icon" aria-hidden="true">
                {t.tone === 'call' ? 'call' : t.icon}
              </span>
            ) : null}
            <p>{t.content}</p>
            {t.undo ? (
              <button
                className="undo"
                type="button"
                onClick={() => {
                  remove(t.id);
                  t.undo?.();
                }}
              >
                戻す
              </button>
            ) : null}
          </div>
        ))}
      </div>

      {dialog ? (
        <div
          className="scrim"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeDialog(false);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') closeDialog(false);
          }}
        >
          <div className="dialog" role="dialog" aria-modal="true">
            <h3>{dialog.title}</h3>
            {dialog.text ? <p>{dialog.text}</p> : null}
            {dialog.body}
            <div className="btns">
              {dialog.cancel !== '' ? (
                <button className="pill-btn ghost" type="button" onClick={() => closeDialog(false)}>
                  {dialog.cancel ?? 'キャンセル'}
                </button>
              ) : null}
              {dialog.ok !== '' ? (
                <button
                  className={`pill-btn ${dialog.tone === 'danger' ? 'danger' : ''}`}
                  type="button"
                  autoFocus
                  onClick={() => closeDialog(true)}
                >
                  {dialog.ok ?? 'OK'}
                </button>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}

      {sheet ? (
        <div
          className="scrim is-sheet"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeSheet(false);
          }}
        >
          <div
            className="sheet"
            role="dialog"
            aria-modal="true"
            style={{ '--c': sheet.color } as React.CSSProperties}
          >
            <div className="grab" />
            <p className="what">{sheet.action}を記録します</p>
            <p className="pt">{sheet.name}</p>
            <p className="pt-id">
              ID：{sheet.patientId}　{sheet.dept}
            </p>
            <button className="pill-btn" type="button" onClick={() => closeSheet(true)}>
              {sheet.action}
            </button>
            <button className="pill-btn ghost" type="button" onClick={() => closeSheet(false)}>
              キャンセル
            </button>
          </div>
        </div>
      ) : null}
    </UiContext.Provider>
  );
}

export function useUi(): UiValue {
  const ctx = useContext(UiContext);
  if (!ctx) throw new Error('UiProvider の外で useUi は使えません');
  return ctx;
}
